import { useCallback, useState, useEffect } from "react";
import { useContractWrite, usePrepareContractWrite, useWaitForTransaction, usePublicClient, useWalletClient, useContractRead } from "wagmi";
import { toast } from "sonner";
import { useForm, FormProvider, useFormContext } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useWalletState } from "@/hooks/useWalletState";
import { CONTRACT_ADDRESSES } from "@/config/addresses";
import { parseEther, parseUnits } from "viem";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { factoryV6ABI, tokenTemplateV6ABI } from "@/config/contracts";
import { FormValues, formSchema } from "./schema";
import { Button } from "@/components/ui/button";
import { Form } from "@/components/ui/form";
import { TokenNameField } from "./fields/TokenNameField";
import { TokenSymbolField } from "./fields/TokenSymbolField";
import { TokenSupplyField } from "./fields/TokenSupplyField";
import { TaxSettings } from "./sections/TaxSettings"; 
import { SecurityFeatures } from "./sections/SecurityFeatures";
import { TaxDistribution } from "./sections/TaxDistribution";
import { InitialLiquidity } from "./sections/InitialLiquidity";
import { Decimal } from 'decimal.js';
import { handleGasError, formatTransactionLink } from '@/utils/gas';
import { formatEther } from 'viem';
import { FeeBreakdown } from './FeeBreakdown';
import { cn } from "@/lib/utils";
import { TokenVerificationStatus } from '../status/TokenVerificationStatus';
import { verifyContract } from '@/utils/verification';
import { decodeEventLog } from 'viem';
import { ADDRESSES } from "@/constants/addresses";
import { PANCAKESWAP_ADDRESSES } from '@/constants/pancakeswap';
import { PANCAKESWAP_ROUTER_ABI, PANCAKESWAP_LOCKER_ABI, PANCAKESWAP_FACTORY_ABI } from '@/constants/pancakeswap';
import { TemplateSelect } from "./fields/TemplateSelect";
import { useAdmin } from '@/hooks/useAdmin';
import { TokenConfirmation } from "./TokenConfirmation";
import { LiquidityConfirmation } from "./LiquidityConfirmation";
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { db, initializeFirebase } from '@/lib/firebase';
import { useFirebaseFees } from '@/providers/FirebaseFeeProvider';
import { useNetwork } from 'wagmi';

interface TokenDetails {
  address: string;
  name: string;
  symbol: string;
  totalSupply: string;
  owner: string;
}

interface LiquidityDetails {
  lpAddress: string;
  tokenAddress: string;
  tokenSymbol: string;
  bnbAmount: string;
  tokenAmount: string;
}

interface FeeData {
  burnFee: string;
  lockFee: string;
  toolsFee: string;
  scanFee: string;
  masterLockFee: string;
  ttTaxDist: string;
  liquidityAddFee: string;
  liquidityRemoveFee: string;
  collectors: { address: string; share: number; }[];
}

const calculateTokenPrice = (bnbAmount: number, tokenAmount: number): number => {
  if (tokenAmount <= 0) return 0;
  return bnbAmount / tokenAmount;
};

const suggestInitialLiquidity = (totalSupply: string): string => {
  const supply = parseFloat(totalSupply || "0");
  // Suggest a reasonable BNB amount based on total supply
  // This will make each token worth approximately 0.0001 BNB initially
  const suggestedBnb = (supply * 0.0001).toFixed(4);
  return suggestedBnb;
};

const calculateSuggestedTokenAmounts = (bnbAmount: number, totalSupply: number): number[] => {
  // Calculate some reasonable token amounts based on BNB provided
  // These will create different price points
  const lowPrice = bnbAmount * 100000;     // 0.000001 BNB per token
  const medPrice = bnbAmount * 10000;      // 0.00001 BNB per token
  const highPrice = bnbAmount * 1000;      // 0.0001 BNB per token
  
  // Return amounts that don't exceed total supply
  return [lowPrice, medPrice, highPrice]
    .filter(amount => amount <= totalSupply)
    .sort((a, b) => a - b);
};

const formatTokenAmount = (amount: number): string => {
  if (amount >= 1000000) {
    return `${(amount / 1000000).toFixed(2)}M tokens`;
  } else if (amount >= 1000) {
    return `${(amount / 1000).toFixed(2)}K tokens`;
  }
  return `${amount.toFixed(2)} tokens`;
};

const getTokenPriceDescription = (bnbAmount: number, tokenAmount: number): string => {
  if (tokenAmount <= 0) return "";
  const pricePerToken = bnbAmount / tokenAmount;
  return `Price per token: ${pricePerToken.toFixed(8)} BNB`;
};

const validateLiquidityRatio = (bnbAmount: number, totalSupply: string): string | null => {
  const supply = parseFloat(totalSupply || "0");
  if (supply <= 0) return null;
  if (bnbAmount <= 0) return null;
  
  const tokenPrice = calculateTokenPrice(bnbAmount, supply);
  
  if (tokenPrice < 0.00000001) {
    return "Warning: Initial token price is very low. Consider adding more BNB or reducing total supply.";
  }
  if (tokenPrice > 1) {
    return "Warning: Initial token price is very high. Consider adding less BNB or increasing total supply.";
  }
  
  return null;
};

export function TokenForm({ hideNetworkStatus = false }: { hideNetworkStatus?: boolean }) {
  const { isConnected, isCorrectChain, chainId, networkName, isTestnet, address } = useWalletState();
  const { data: walletClient } = useWalletClient();
  const publicClient = usePublicClient();
  const [createdToken, setCreatedToken] = useState<{ address: string; isVerified: boolean } | null>(null);
  const [createdTokenDetails, setCreatedTokenDetails] = useState<TokenDetails | null>(null);
  const [liquidityDetails, setLiquidityDetails] = useState<LiquidityDetails | null>(null);
  const { isAdmin, isInitialized: isAdminInitialized } = useAdmin();
  const { settings: firebaseSettings, loading: firebaseLoading } = useFirebaseFees();
  const { chainId: networkChainId } = useNetwork();
  const { chain } = useNetwork();

  // Helper function to get network-specific currency name
  const getNetworkCurrency = (chainId?: number) => {
    switch (chainId) {
      case 1:
      case 11155111:
        return 'ETH';
      case 43114:
      case 43113:
        return 'AVAX';
      case 56:
      case 97:
      case 204: // opBNB Mainnet
      case 5611: // opBNB Testnet
        return 'BNB';
      case 11124:
      case 2741:
        return 'ETH'; // Abstract uses ETH
      case 1328:
      case 1329:
        return 'SEI'; // Sei network
      case 8453:
      case 84532:
        return 'ETH'; // Base uses ETH
      case 10:
      case 11155420:
        return 'ETH'; // Optimism uses ETH
      case 42161:
      case 421614:
        return 'ETH'; // Arbitrum uses ETH
      case 137:
      case 80002:
        return 'MATIC'; // Polygon uses MATIC
      default:
        return 'native tokens';
    }
  };

  // Read platform fee from contract
  const { data: platformFee } = useContractRead({
    address: CONTRACT_ADDRESSES[chainId as keyof typeof CONTRACT_ADDRESSES]?.factory,
    abi: factoryV6ABI,
    functionName: 'platformFee',
    chainId: chainId,
    enabled: Boolean(chainId && isConnected),
    watch: false,
    cacheTime: 30_000, // Cache for 30 seconds
    staleTime: 30_000, // Consider data stale after 30 seconds
    onError: (error) => {
      console.error('Error reading platform fee:', error);
    },
    onSuccess: (data) => {
      if (process.env.NODE_ENV === 'development') {
        console.debug('Platform fee updated:', {
          chainId,
          fee: data,
        });
      }
    }
  });

  // Add debug logging for BSC mainnet
  useEffect(() => {
    if (chainId === 56) {
      console.log('BSC Mainnet Debug:', {
        factoryAddress: CONTRACT_ADDRESSES[56].factory,
        platformFee,
        isConnected,
        chainId
      });
    }
  }, [chainId, platformFee, isConnected]);

  // Read current template address from factory
  const { data: currentTemplate } = useContractRead({
    address: chainId ? CONTRACT_ADDRESSES[chainId as keyof typeof CONTRACT_ADDRESSES]?.factory : undefined,
    abi: factoryV6ABI,
    functionName: 'templateAddress',
    enabled: Boolean(chainId && isConnected && isCorrectChain),
    watch: true,
  });

  useEffect(() => {
    if (currentTemplate) {
      console.log('Current template address:', currentTemplate);
    }
  }, [currentTemplate]);

  // Form setup
  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    mode: "onChange",
    defaultValues: {
      name: "",
      symbol: "",
      decimals: "18",
      totalSupply: "",
      buyTax: "0",
      sellTax: "0",
      taxDistribution: {
        reflection: "0",
        autoLiquidity: "0",
        burn: "0",
        marketing: "0",
        development: "0"
      },
      marketingWallets: address ? [address] : [""],
      developmentWallets: address ? [address] : [""],
      maxWalletPercentage: "0", // No limit
      antiDump: {
        maxBuyPercentage: "0", // No limit
        maxSellPercentage: "0", // No limit
        enabled: false
      },
      tradingDelay: "0", // No delay
      tradingEnabled: true, // Enable trading on deploy
      contractLocked: false, // Contract lock disabled
      permanentTrading: false, // Permanent trading disabled
      initialLiquidity: {
        bnbAmount: "0", // Set default to "No initial liquidity"
        tokenAmount: "",
        customAmount: "",
        customTokenAmount: "",
        lockDuration: "0"
      }
    }
  });

  // Update wallet addresses when connected address changes, but only if they match the old address
  useEffect(() => {
    if (address) {
      const currentMarketingWallets = form.getValues("marketingWallets");
      const currentDevelopmentWallets = form.getValues("developmentWallets");

      // Only update if the current wallet matches the old connected wallet
      const oldAddress = currentMarketingWallets[0];
      if (currentMarketingWallets.length === 1 && oldAddress === form.formState.defaultValues?.marketingWallets?.[0]) {
        form.setValue("marketingWallets", [address]);
      }
      if (currentDevelopmentWallets.length === 1 && currentDevelopmentWallets[0] === form.formState.defaultValues?.developmentWallets?.[0]) {
        form.setValue("developmentWallets", [address]);
      }
    }
  }, [address]);

  // Only log form state when it actually changes
  useEffect(() => {
    const formIsValid = form.formState.isValid;
    const hasErrors = Object.keys(form.formState.errors).length > 0;
    
    if (hasErrors) {
      console.log('Form validation errors:', form.formState.errors);
    }
  }, [form.formState.errors]);

  const calculateTokenAmount = (amount: string) => {
    try {
      // Convert the amount to BigInt, handling both decimal and non-decimal inputs
      if (amount.includes('e')) {
        // Handle scientific notation
        const [base, exponent] = amount.split('e');
        const baseNum = parseFloat(base);
        const exp = parseInt(exponent);
        amount = (baseNum * Math.pow(10, exp)).toString();
      }
      return parseUnits(amount || "0", 18);
    } catch (error) {
      console.error("Error calculating token amount:", error);
      return BigInt(0);
    }
  };

  // Define fee constants
  const [feeData, setFeeData] = useState<FeeData>({
    burnFee: '0',
    lockFee: '0',
    toolsFee: '0',
    scanFee: '0',
    masterLockFee: '0',
    ttTaxDist: '0',
    liquidityAddFee: '5',  // Default value if Firebase fails
    liquidityRemoveFee: '2.5',
    collectors: [{ address: '', share: 100 }]
  });

  // Add state for Firebase fees
  const [firebaseFees, setFirebaseFees] = useState<FeeData | null>(null);
  const [isLoadingFees, setIsLoadingFees] = useState(false);

  // Fetch Firebase fees only when initial liquidity is being added
  useEffect(() => {
    const fetchFees = async () => {
      const bnbAmount = form.watch("initialLiquidity.bnbAmount");
      // Only fetch if initial liquidity is being added
      if (!bnbAmount || bnbAmount === "0" || bnbAmount === "no_initial_liquidity") {
        return;
      }

      try {
        setIsLoadingFees(true);
        const feesDoc = await getDoc(doc(db, 'fees', 'current'));
        if (feesDoc.exists()) {
          const data = feesDoc.data() as FeeData;
          console.debug('Fetched Firebase fees:', data);
          setFirebaseFees(data);
        } else {
          console.debug('No fees document found in Firebase');
        }
      } catch (error) {
        console.error('Error fetching fees:', error);
      } finally {
        setIsLoadingFees(false);
      }
    };

    fetchFees();
  }, [form.watch("initialLiquidity.bnbAmount")]);

  // Get base deploy fee from contract
  const baseDeployFee = platformFee ? Number(formatEther(platformFee)) : 0;

  // Calculate liquidity fee using Firebase settings
  const calculateLiquidityFee = useCallback(() => {
    const bnbAmount = form.watch("initialLiquidity.bnbAmount");
    if (!bnbAmount || bnbAmount === "0" || bnbAmount === "no_initial_liquidity") return 0;
    
    const customAmount = form.getValues("initialLiquidity.customAmount");
    const actualAmount = bnbAmount === "custom"
      ? parseFloat(customAmount || "0")
      : parseFloat(bnbAmount);

    if (isNaN(actualAmount) || actualAmount <= 0) return 0;
    
    // Get the fee from Firebase settings
    const networkFees = firebaseSettings?.networkFees[networkChainId || 56];
    const addLiquidityFee = networkFees?.addLiquidityFee 
      ? parseFloat(networkFees.addLiquidityFee) / 100 
      : 0;
    
    console.debug('Liquidity Fee Calculation:', {
      networkFees,
      addLiquidityFee,
      initialAmount: actualAmount,
      calculatedFee: actualAmount * addLiquidityFee
    });
    
    const fee = actualAmount * addLiquidityFee;
    return isNaN(fee) ? 0 : fee;
  }, [form, firebaseSettings, networkChainId]);

  // Get initial liquidity amount
  const getInitialLiquidityAmount = () => {
    const bnbAmount = form.watch("initialLiquidity.bnbAmount");
    
    if (!bnbAmount || bnbAmount === "0") return 0;
    
    const customAmount = form.watch("initialLiquidity.customAmount");
    const actualAmount = bnbAmount === "custom" ? parseFloat(customAmount || "0") : parseFloat(bnbAmount);
      
    return isNaN(actualAmount) ? 0 : actualAmount;
  };

  // Calculate estimated gas based on network and contract complexity
  const calculateEstimatedGas = useCallback(async () => {
    if (!publicClient || !chainId) return 0;

    try {
      // Check if we've calculated gas price recently for this chain
      const lastGasCheck = localStorage.getItem(`lastGasCheck_${chainId}`);
      const lastGasPrice = localStorage.getItem(`lastGasPrice_${chainId}`);
      
      // If we have a recent calculation (within 30 seconds), use cached value
      if (lastGasCheck && lastGasPrice) {
        const lastCheckTime = parseInt(lastGasCheck);
        if (Date.now() - lastCheckTime < 30000) { // 30 seconds cache
          console.log('Using cached gas price for', chainId);
          return parseFloat(lastGasPrice);
        }
      }
      
      // Get current gas price from WalletConnect infrastructure
      const gasPrice = await publicClient.getGasPrice();
      
      // Convert to ETH units
      const gasPriceInEth = Number(formatEther(gasPrice));
      
      // Base gas units for token deployment (empirically measured)
      let baseGasUnits = 2000000; // Standard gas units for token deployment
      
      // Add gas for each feature enabled
      const buyTax = parseFloat(form.watch("buyTax") || "0");
      const sellTax = parseFloat(form.watch("sellTax") || "0");
      const hasReflection = parseFloat(form.watch("taxDistribution.reflection") || "0") > 0;
      const hasAutoLiquidity = parseFloat(form.watch("taxDistribution.autoLiquidity") || "0") > 0;
      
      if (buyTax > 0 || sellTax > 0) baseGasUnits += 100000;
      if (hasReflection) baseGasUnits += 200000;
      if (hasAutoLiquidity) baseGasUnits += 150000;

      // Calculate total gas cost in native token
      let gasInNative = gasPriceInEth * baseGasUnits;
      
      // Special handling for Sepolia - cap the gas price to prevent extreme values
      if (chainId === 11155111) {
        // If gas price seems too high for Sepolia, cap it
        if (gasPriceInEth > 0.0000001) { // 100 Gwei in ETH
          console.log('Capping extreme Sepolia gas price', gasPriceInEth);
          gasInNative = 0.0000001 * baseGasUnits;
        }
      }
      
      console.log('Gas estimation details:', {
        chainId,
        gasPriceInEth,
        baseGasUnits,
        gasInNative,
        withBuffer: gasInNative * 1.2
      });
      
      // Cache the calculation
      localStorage.setItem(`lastGasCheck_${chainId}`, Date.now().toString());
      localStorage.setItem(`lastGasPrice_${chainId}`, (gasInNative * 1.2).toString());
      
      // Add 20% buffer for safety
      return gasInNative * 1.2;
    } catch (error) {
      console.error("Error estimating gas:", error);
      // Fallback to conservative estimates based on chain
      if (chainId === 11155111) return 0.0005; // Sepolia
      if (chainId === 11124) return 0.0005; // Abstract Testnet
      if (chainId === 97) return 0.005; // BSC Testnet
      if (chainId === 56) return 0.008; // BSC Mainnet
      if (chainId === 43113) return 0.01; // Avalanche Fuji
      if (chainId === 43114) return 0.015; // Avalanche Mainnet
      return 0.01; // Default fallback
    }
  }, [chainId, publicClient, form]);

  // Update estimated gas when form values change or network changes
  const [estimatedGasInBNB, setEstimatedGasInBNB] = useState(0);
  const [lastNetworkId, setLastNetworkId] = useState<number | null>(null);

  useEffect(() => {
    // If network changed, force recalculation
    if (chainId !== lastNetworkId) {
      setLastNetworkId(chainId);
      // Clear cached gas price for this network to force fresh calculation
      if (chainId) {
        localStorage.removeItem(`lastGasCheck_${chainId}`);
        localStorage.removeItem(`lastGasPrice_${chainId}`);
      }
    }
    
    // Debounce the gas calculation to prevent rapid recalculations
    const timer = setTimeout(() => {
      calculateEstimatedGas().then(setEstimatedGasInBNB);
    }, 500); // 500ms debounce
    
    return () => clearTimeout(timer);
  }, [
    calculateEstimatedGas,
    chainId,
    form.watch("buyTax"),
    form.watch("sellTax"),
    form.watch("taxDistribution.reflection"),
    form.watch("taxDistribution.autoLiquidity")
  ]);

  // Get fee breakdown values for display
  const getFeeBreakdownValues = () => {
    const liquidityAmount = getInitialLiquidityAmount();
    const liquidityFee = calculateLiquidityFee();
    
    // Ensure all values are valid numbers
    const safeBaseDeployFee = isNaN(baseDeployFee) ? 0 : baseDeployFee;
    const safeLiquidityAmount = isNaN(liquidityAmount) ? 0 : liquidityAmount;
    const safeLiquidityFee = isNaN(liquidityFee) ? 0 : liquidityFee;
    const safeGasFee = isNaN(estimatedGasInBNB) ? 0 : estimatedGasInBNB * 2.3;
    
    const total = safeBaseDeployFee + safeLiquidityAmount + safeLiquidityFee + safeGasFee;

    return {
      baseDeployFee: safeBaseDeployFee,
      liquidityFee: safeLiquidityFee,
      estimatedGas: safeGasFee,
      totalBnbRequired: Number(total.toFixed(4)),
      initialLiquidity: safeLiquidityAmount
    };
  };

  // Calculate total fees
  const calculateTotalFees = () => {
    return baseDeployFee + calculateLiquidityFee();
  };

  // Calculate total BNB required with accurate gas estimation
  const calculateTotalBnbRequired = useCallback(() => {
    const liquidityAmount = getInitialLiquidityAmount();
    const liquidityFee = calculateLiquidityFee();
    const safeBaseDeployFee = isNaN(baseDeployFee) ? 0 : baseDeployFee;
    const safeLiquidityAmount = isNaN(liquidityAmount) ? 0 : liquidityAmount;
    const safeLiquidityFee = isNaN(liquidityFee) ? 0 : liquidityFee;
    
    // Use real-time gas estimation
    const safeGasFee = isNaN(estimatedGasInBNB) ? 0 : estimatedGasInBNB;

    const total = safeBaseDeployFee + safeLiquidityAmount + safeLiquidityFee + safeGasFee;
    return Number(total.toFixed(4));
  }, [baseDeployFee, getInitialLiquidityAmount, calculateLiquidityFee, estimatedGasInBNB]);

  const { config, error: prepareError } = usePrepareContractWrite({
    address: chainId ? CONTRACT_ADDRESSES[chainId as keyof typeof CONTRACT_ADDRESSES]?.factory : undefined,
    abi: factoryV6ABI,
    functionName: 'deployToken',
    args: chainId ? [
      form.watch("name") || "",  // name
      form.watch("symbol") || "", // symbol
      calculateTokenAmount(form.watch("totalSupply") || "0"), // totalSupply
      BigInt(Math.floor(parseFloat(
        form.watch("buyTax") === "custom" 
          ? form.watch("customBuyTax") || "0"
          : form.watch("buyTax") || "0"
      ))), // buyTax in percentage (not basis points)
      BigInt(Math.floor(parseFloat(
        form.watch("sellTax") === "custom"
          ? form.watch("customSellTax") || "0"
          : form.watch("sellTax") || "0"
      ))), // sellTax in percentage (not basis points)
      [ // taxDistribution
        BigInt(Math.floor(parseFloat(
          form.watch("taxDistribution.reflection") === "custom"
            ? form.watch("taxDistribution.customReflection") || "0"
            : form.watch("taxDistribution.reflection") || "0"
        ))),
        BigInt(Math.floor(parseFloat(
          form.watch("taxDistribution.autoLiquidity") === "custom"
            ? form.watch("taxDistribution.customAutoLiquidity") || "0"
            : form.watch("taxDistribution.autoLiquidity") || "0"
        ))),
        BigInt(Math.floor(parseFloat(
          form.watch("taxDistribution.burn") === "custom"
            ? form.watch("taxDistribution.customBurn") || "0"
            : form.watch("taxDistribution.burn") || "0"
        ))),
        BigInt(Math.floor(parseFloat(
          form.watch("taxDistribution.marketing") === "custom"
            ? form.watch("taxDistribution.customMarketing") || "0"
            : form.watch("taxDistribution.marketing") || "0"
        ))),
        BigInt(Math.floor(parseFloat(
          form.watch("taxDistribution.development") === "custom"
            ? form.watch("taxDistribution.customDevelopment") || "0"
            : form.watch("taxDistribution.development") || "0"
        )))
      ],
      form.watch("marketingWallets") || [address || ""], // marketingWallets
      form.watch("developmentWallets") || [address || ""], // developmentWallets
      // Convert "No Limit" (10000) to basis points directly, otherwise convert percentage to basis points
      BigInt(form.watch("maxWalletPercentage") === "10000" ? 10000 : Math.floor(parseFloat(form.watch("maxWalletPercentage") || "0") * 100)), // maxWalletPercent in basis points
      BigInt(form.watch("antiDump.maxBuyPercentage") === "10000" ? 10000 : Math.floor(parseFloat(form.watch("antiDump.maxBuyPercentage") || "0") * 100)), // maxBuyPercent in basis points
      BigInt(form.watch("antiDump.maxSellPercentage") === "10000" ? 10000 : Math.floor(parseFloat(form.watch("antiDump.maxSellPercentage") || "0") * 100)), // maxSellPercent in basis points
      BigInt(0), // tradingDelay
      [ // locks array
        form.watch("taxCollectionLocked") || false,
        form.watch("taxDistributionLocked") || false,
        false, // maxWalletLocked
        false, // maxTxLocked
        form.watch("contractLocked") || false,  // Contract lock status from form
      ],
      // Use the correct router address based on the chain
      CONTRACT_ADDRESSES[chainId as keyof typeof CONTRACT_ADDRESSES]?.router || "0x0000000000000000000000000000000000000000",
      form.watch("permanentTrading") || false,  // Permanent trading parameter
    ] : undefined,
    value: platformFee || BigInt(0),
    enabled: Boolean(
      chainId && 
      isConnected && 
      isCorrectChain && 
      form.formState.isValid && 
      platformFee !== undefined
    ),
  });

  const maxWalletPercent = BigInt(Math.floor(parseFloat(form.watch("maxWalletPercentage") || "0") * 100));
  const maxBuyPercent = BigInt(Math.floor(parseFloat(form.watch("antiDump.maxBuyPercentage") || "0") * 100));
  const maxSellPercent = BigInt(Math.floor(parseFloat(form.watch("antiDump.maxSellPercentage") || "0") * 100));

  // Debug logging for prepare errors
  useEffect(() => {
    if (prepareError) {
      console.error('Prepare contract write error:', prepareError);
    }
  }, [prepareError]);

  useEffect(() => {
    console.log('Contract write preparation:', {
      enabled: Boolean(chainId && isConnected && isCorrectChain && platformFee !== undefined),
      chainId,
      isConnected,
      isCorrectChain,
      platformFee,
      factoryAddress: CONTRACT_ADDRESSES[chainId as keyof typeof CONTRACT_ADDRESSES]?.factory,
      hasConfig: !!config,
      prepareError: prepareError?.message
    });
  }, [chainId, isConnected, isCorrectChain, platformFee, config, prepareError]);

  const { data: hash, isLoading, write: createToken, error: writeError } = useContractWrite({
    ...config,
    onSuccess: async (tx) => {
      toast.success(
        <div>
          Transaction submitted!
          <br />
          <a 
            href={formatTransactionLink(tx.hash, chainId)} 
            target="_blank" 
            rel="noopener noreferrer"
            className="underline"
          >
            View on {isTestnet ? 'TestBSCScan' : 'BSCScan'}
          </a>
        </div>
      );

      try {
        const receipt = await publicClient.waitForTransactionReceipt({ hash: tx.hash });
        console.log("Full transaction receipt:", receipt);

        // Find the TokenDeployed event
        const tokenDeployedEvent = receipt.logs.find(log => {
          try {
            const decoded = decodeEventLog({
              abi: factoryV6ABI,
              data: log.data,
              topics: log.topics,
            });
            console.log("Decoded log:", decoded);
            return decoded.eventName === 'TokenDeployed';
          } catch (e) {
            console.log("Failed to decode log:", e);
            return false;
          }
        });

        if (tokenDeployedEvent) {
          console.log("Found TokenDeployed event:", tokenDeployedEvent);
          
          const decodedEvent = decodeEventLog({
            abi: factoryV6ABI,
            data: tokenDeployedEvent.data,
            topics: tokenDeployedEvent.topics,
          });
          
          console.log("Decoded TokenDeployed event:", decodedEvent);
          
          // Get the token address from the correct event parameter
          const deployedTokenAddress = (decodedEvent.args as any).tokenAddress;
          console.log("Deployed token address:", deployedTokenAddress);

          if (!deployedTokenAddress) {
            throw new Error("Token address not found in event");
          }
          
          // Set the created token details
          setCreatedTokenDetails({
            address: deployedTokenAddress,
            name: form.getValues("name"),
            symbol: form.getValues("symbol"),
            totalSupply: form.getValues("totalSupply"),
            owner: address || ""
          });
          
          toast.success("Token created successfully!");

          // Persist token metadata to Firestore for admin tools and discovery
          try {
            const { db: firebaseDb } = await initializeFirebase();
            const tokenDocRef = doc(
              firebaseDb,
              'tokens',
              `${chainId || 0}_${String(deployedTokenAddress).toLowerCase()}`
            );

            await setDoc(tokenDocRef, {
              address: deployedTokenAddress,
              name: form.getValues("name"),
              symbol: form.getValues("symbol"),
              totalSupply: form.getValues("totalSupply"),
              owner: address || "",
              chainId: chainId || null,
              chainName: chain?.name || null,
              createdAt: new Date().toISOString(),
            }, { merge: true });
          } catch (error) {
            console.error('Failed to save token metadata to Firestore:', error);
          }

          // If initial liquidity is set, add it
          const bnbAmount = form.watch("initialLiquidity.bnbAmount");
          if (bnbAmount && bnbAmount !== "0") {
            const liquidityAmount = bnbAmount === "custom"
              ? parseFloat(form.watch("initialLiquidity.customAmount") || "0")
              : parseFloat(bnbAmount);

            const lockDuration = form.watch("initialLiquidity.lockDuration");
            const lockTime = lockDuration === "custom"
              ? parseInt(form.watch("initialLiquidity.customLockDuration") || "0")
              : parseInt(lockDuration || "0");

            try {
              if (!walletClient || !chainId) {
                throw new Error("Wallet not connected");
              }

              const chainAddresses = PANCAKESWAP_ADDRESSES[chainId];
              if (!chainAddresses) {
                throw new Error("Chain not supported");
              }

              const isEthereumNetwork = chainId === 1 || chainId === 11155111;
              const dexName = isEthereumNetwork ? 'Uniswap' : 'PancakeSwap';

              // First approve the router to spend tokens
              const tokenContract = {
                address: deployedTokenAddress as `0x${string}`,
                abi: [{
                  name: 'approve',
                  type: 'function',
                  stateMutability: 'nonpayable',
                  inputs: [{ name: 'spender', type: 'address' }, { name: 'amount', type: 'uint256' }],
                  outputs: [{ name: '', type: 'bool' }]
                }]
              };

              // Get the token amount for liquidity from the form
              const bnbAmount = form.watch("initialLiquidity.bnbAmount");
              const customBnbAmount = form.watch("initialLiquidity.customAmount");
              const tokenAmount = form.watch("initialLiquidity.tokenAmount");
              const customTokenAmount = form.watch("initialLiquidity.customTokenAmount");

              // Calculate actual amounts
              const actualBnbAmount = bnbAmount === "custom" ? customBnbAmount : bnbAmount;
              const actualTokenAmount = tokenAmount === "custom" ? customTokenAmount : tokenAmount;

              if (!actualTokenAmount || !actualBnbAmount) {
                throw new Error("Token amount or BNB amount not set");
              }

              const tokenAmountWei = parseEther(actualTokenAmount.toString());

              console.log(`Approving ${dexName} router...`);
              const approvalToast = toast.loading(`Approving ${dexName} router...`);
              const approveTx = await walletClient.writeContract({
                ...tokenContract,
                functionName: 'approve',
                args: [
                  chainAddresses.ROUTER,
                  tokenAmountWei // Only approve the amount we're adding to liquidity
                ]
              });

              await publicClient.waitForTransactionReceipt({ hash: approveTx });
              console.log("Router approved");
              toast.dismiss(approvalToast);

              // Add liquidity using wagmi
              console.log("Adding liquidity...");
              const liquidityToast = toast.loading("Adding initial liquidity...");
              try {
                console.log("Preparing liquidity parameters:", {
                  tokenAddress: deployedTokenAddress,
                  tokenAmount: actualTokenAmount,
                  bnbAmount: actualBnbAmount,
                  address
                });

                const minTokenAmount = tokenAmountWei; // Set min tokens to the full amount
                const minEthAmount = parseEther(actualBnbAmount.toString()); // Set min ETH to the full amount
                const deadline = BigInt(Math.floor(Date.now() / 1000) + 1200); // 20 minutes

                console.log("Transaction parameters:", {
                  token: deployedTokenAddress,
                  tokenAmount: tokenAmountWei.toString(),
                  minTokenAmount: minTokenAmount.toString(),
                  minEthAmount: minEthAmount.toString(),
                  recipient: address,
                  deadline: deadline.toString()
                });

                const addLiquidityTx = await walletClient.writeContract({
                  address: chainAddresses.ROUTER,
                  abi: PANCAKESWAP_ROUTER_ABI,
                  functionName: 'addLiquidityETH',
                  args: [
                    deployedTokenAddress,
                    tokenAmountWei,
                    minTokenAmount,
                    minEthAmount,
                    address,
                    deadline
                  ],
                  value: parseEther(actualBnbAmount.toString())
                });
                
                if (typeof addLiquidityTx === 'string') {
                  const receipt = await publicClient.waitForTransactionReceipt({ 
                    hash: addLiquidityTx as `0x${string}`
                  });
                  console.log("Liquidity transaction receipt:", receipt);
                  
                  if (receipt.status === 'success') {
                    toast.dismiss(liquidityToast);
                    toast.success("Liquidity added successfully!");
                    
                    console.log("Getting LP address...");
                    
                    // Get the LP token address from the factory
                    const factoryContract = {
                      address: chainAddresses.FACTORY as `0x${string}`,
                      abi: PANCAKESWAP_FACTORY_ABI
                    };

                    console.log("Factory contract config:", {
                      address: factoryContract.address,
                      token: deployedTokenAddress,
                      weth: chainAddresses.WBNB
                    });

                    try {
                      // Get pair address directly from the factory
                      const lpAddress = await publicClient.readContract({
                        ...factoryContract,
                        functionName: 'getPair',
                        args: [deployedTokenAddress as `0x${string}`, chainAddresses.WBNB]
                      }) as `0x${string}`;

                      console.log("Got LP address:", lpAddress);

                      if (lpAddress && lpAddress !== '0x0000000000000000000000000000000000000000') {
                        console.log("Setting liquidity details with:", {
                          lpAddress,
                          tokenAddress: deployedTokenAddress,
                          tokenSymbol: form.watch("symbol"),
                          bnbAmount: actualBnbAmount.toString(),
                          tokenAmount: actualTokenAmount.toString()
                        });

                        // Set the liquidity details state
                        setLiquidityDetails({
                          lpAddress,
                          tokenAddress: deployedTokenAddress,
                          tokenSymbol: form.watch("symbol"),
                          bnbAmount: actualBnbAmount.toString(),
                          tokenAmount: actualTokenAmount.toString()
                        });

                        toast.success("Liquidity added successfully!");
                      } else {
                        console.error("Invalid LP address:", lpAddress);
                        throw new Error("Failed to get LP address - address is empty");
                      }
                    } catch (error) {
                      console.error("Failed to get LP address:", error);
                      toast.dismiss(liquidityToast);
                      toast.error("Failed to get LP token address. The liquidity might still have been added successfully.");
                    }
                  } else {
                    throw new Error("Liquidity transaction failed");
                  }
                } else {
                  throw new Error("Invalid transaction hash returned");
                }
              } catch (error: any) {
                toast.dismiss(liquidityToast);
                toast.error(`Failed to add liquidity: ${error.message || 'Unknown error'}`);
                return;
              }

              // If lock duration is set, lock the liquidity
              if (lockTime > 0) {
                toast.loading("Locking liquidity...");
                const lockTx = await walletClient.writeContract({
                  address: chainAddresses.LOCKER,
                  abi: PANCAKESWAP_LOCKER_ABI,
                  functionName: 'lockLiquidity',
                  args: [
                    deployedTokenAddress,
                    BigInt(lockTime * 24 * 60 * 60), // convert days to seconds
                  ],
                  value: parseEther("0.01") // small fee for locking
                });

                await publicClient.waitForTransactionReceipt({ hash: lockTx });
                toast.success("Liquidity locked successfully!");
              }
            } catch (error) {
              console.error("Liquidity error:", error);
              toast.error("Failed to add initial liquidity. Please try manually.");
            }
          }
        } else {
          console.error("TokenDeployed event not found in transaction logs");
          console.log("All transaction logs:", receipt.logs);
          toast.error("Could not find token address in transaction");
        }
      } catch (error) {
        console.error("Error processing transaction receipt:", error);
        toast.error("Error processing transaction receipt");
      }
    },
    onError: (error) => {
      const insufficientBalanceMatch = error.message.match(/insufficient funds for gas \* price \+ value/i);
      if (insufficientBalanceMatch) {
        const currency = getNetworkCurrency(chain?.id);
        const requiredAmount = platformFee ? formatEther(platformFee) : '0';
        toast.error(
          `Insufficient ${currency} balance. Required: ${requiredAmount} ${currency}`,
          { duration: 5000 }
        );
        return;
      }
      toast.error(handleGasError(error));
    }
  });

  const onSubmit = useCallback(async (data: any) => {
    try {
      console.log("Submitting form with data:", data);
      console.log("Platform fee:", baseDeployFee, "BNB");

      // Ensure we have enough BNB to cover all costs
      const totalRequired = calculateTotalBnbRequired();
      const balance = address ? await publicClient.getBalance({ address }) : BigInt(0);
      const balanceInEther = Number(formatEther(balance));

      if (balanceInEther < totalRequired) {
        const currency = getNetworkCurrency(chain?.id);
        const requiredAmount = platformFee ? formatEther(platformFee) : '0';
        toast.error(
          `Insufficient ${currency} balance. Required: ${requiredAmount} ${currency}`,
          { duration: 5000 }
        );
        return;
      }

      if (!createToken) {
        throw new Error("Write function not ready");
      }

      createToken();
    } catch (error) {
      console.error(" Form submission error:", error);
      toast.error("Failed to create token. Please try again.");
    }
  }, [address, publicClient, baseDeployFee, calculateTotalBnbRequired, createToken]);

  const { isLoading: isWaitingForReceipt } = useWaitForTransaction({
    hash,
    onSuccess: async (receipt) => {
      // Decode events from receipt
      const events = receipt.logs.map(log => {
        try {
          return decodeEventLog({
            abi: factoryV6ABI,
            data: log.data,
            topics: log.topics,
          });
        } catch {
          return null;
        }
      }).filter(Boolean);

      // Find TokenDeployed event
      const tokenCreatedEvent = events.find(event => 
        event.eventName === 'TokenDeployed'
      );

      // Get token address from event or logs
      let tokenAddress = null;
      if (tokenCreatedEvent) {
        tokenAddress = tokenCreatedEvent.args?.tokenAddress;
      } else {
        // Fallback: try to find the token address from the raw logs
        const tokenDeployedLog = receipt.logs.find(log => 
          log.topics[0] === "0xf9a44e6db3fb6e0eb31c4013bda8c662fecef1768dd2412270cc8f8821cbccf3"
        );
        if (tokenDeployedLog?.topics[1]) {
          tokenAddress = `0x${tokenDeployedLog.topics[1].slice(26)}`;
        }
      }

      if (tokenAddress) {
        setCreatedToken({ address: tokenAddress, isVerified: false });

        // Persist token metadata to Firestore (receipt-based flow)
        try {
          const { db: firebaseDb } = await initializeFirebase();
          const tokenDocRef = doc(
            firebaseDb,
            'tokens',
            `${chainId || 0}_${String(tokenAddress).toLowerCase()}`
          );

          await setDoc(tokenDocRef, {
            address: tokenAddress,
            name: form.getValues("name"),
            symbol: form.getValues("symbol"),
            totalSupply: form.getValues("totalSupply"),
            owner: address || "",
            chainId: chainId || null,
            chainName: chain?.name || null,
            createdAt: new Date().toISOString(),
          }, { merge: true });

          console.log('Saved token metadata to Firestore (receipt flow):', tokenAddress);
        } catch (error) {
          console.error('Failed to save token metadata to Firestore (receipt flow):', error);
          toast.error('Token created, but failed to save metadata to Firebase. See console for details.');
        }

        toast.success(
          <div>
            Token created successfully!
            <br />
            Contract Address: {tokenAddress}
          </div>
        );

        // Verify the contract
        console.log("Verifying contract...");
        const verificationToast = toast.loading("Verifying contract...");
        const verified = await verifyContract(
          tokenAddress,
          form.getValues("name"),
          form.getValues("symbol"),
          BigInt(parseEther(form.getValues("totalSupply"))),
          form.getValues("buyTax"),
          form.getValues("sellTax"),
          form.getValues("maxWalletPercent"),
          form.getValues("maxBuyPercent"),
          form.getValues("maxSellPercent"),
          form.getValues("tradingDelay"),
          PANCAKESWAP_ADDRESSES[chainId].ROUTER,
          address || "",
          chainId
        );
        toast.dismiss(verificationToast);
        if (verified) {
          toast.success("Contract verified successfully!");
        } else {
          toast.error("Contract verification failed. Please verify manually.");
        }
      } else {
        toast.error("Token created but address not found in transaction");
      }
    }
  });

  const onTotalSupplyChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    form.setValue("totalSupply", value);
    
    // Suggest initial liquidity when total supply changes
    const suggestedLiquidity = suggestInitialLiquidity(value);
    toast.info(`Suggested initial liquidity: ${suggestedLiquidity} BNB`);
  };

  return (
    <div className="space-y-8">
      <FormProvider {...form}>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            {/* Token Template Selection */}
            <div className="relative rounded-lg mb-6 border-2 border-[var(--color-header-accent,#0A74FF)]">
              <div className="relative bg-background/30 backdrop-blur-sm rounded-lg">
                <div className="p-6">
                  <h2 className="text-2xl font-semibold">Choose a Token Template</h2>
                  <p className="text-muted-foreground mb-6">Select a template to get started with your token</p>
                  <TemplateSelect />
                </div>
              </div>
            </div>

            {/* Basic Information */}
            <div>
              <p className="text-center text-green-500 mb-2">The Basic Information Form is Mandatory.</p>
              <p className="text-center text-muted-foreground mb-6">
                Everything else is optional, & modifiable using our token tools after deployment
              </p>
              
              <div className="relative rounded-lg border-2 border-[var(--color-header-accent,#0A74FF)]">
                <div className="relative bg-background/30 backdrop-blur-sm rounded-lg">
                  <div className="p-6">
                    <h2 className="text-2xl font-semibold text-center mb-4">Basic Information</h2>
                    <p className="text-muted-foreground text-center mb-6">Enter the basic details of your token</p>
                    
                    <div className="space-y-6">
                      <div className="space-y-4">
                        <div>
                          <TokenNameField />
                        </div>

                        <div>
                          <TokenSymbolField />
                        </div>

                        <div>
                          <TokenSupplyField />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Tax Collection Card */}
            <div className="relative rounded-lg mb-6 border-2 border-[var(--color-header-accent,#0A74FF)]">
              <div className="relative bg-background/30 backdrop-blur-sm rounded-lg">
                <div className="p-6">
                  <h2 className="text-2xl font-semibold">Tax Collection</h2>
                  <p className="text-muted-foreground mb-6">Configure your token's tax collection settings</p>
                  <TaxSettings />
                </div>
              </div>
            </div>

            {/* Tax Distribution Card */}
            <div className="relative rounded-lg mb-6 border-2 border-[var(--color-header-accent,#0A74FF)]">
              <div className="relative bg-background/30 backdrop-blur-sm rounded-lg">
                <div className="p-6">
                  <h2 className="text-2xl font-semibold">Tax Distribution</h2>
                  <p className="text-muted-foreground mb-6">Configure how the collected tax is distributed</p>
                  <TaxDistribution />
                </div>
              </div>
            </div>

            {/* Security Features Card */}
            <div className="relative rounded-lg mb-6 border-2 border-[var(--color-header-accent,#0A74FF)]">
              <div className="relative bg-background/30 backdrop-blur-sm rounded-lg">
                <div className="p-6">
                  <h2 className="text-2xl font-semibold">Security Features</h2>
                  <p className="text-muted-foreground mb-6">Configure your token's security settings</p>
                  <SecurityFeatures />
                </div>
              </div>
            </div>

            {/* Initial Liquidity Card */}
            <div className="relative rounded-lg mb-6 border-2 border-[var(--color-header-accent,#0A74FF)]">
              <div className="relative bg-background/30 backdrop-blur-sm rounded-lg">
                <div className="p-6">
                  <h2 className="text-2xl font-semibold">Initial Liquidity</h2>
                  <p className="text-muted-foreground mb-6">Configure your token's initial liquidity</p>
                  <InitialLiquidity />
                </div>
              </div>
            </div>

            {/* Summary and Actions Card */}
            <div className="relative rounded-lg border-2 border-[var(--color-header-accent,#0A74FF)]">
              <div className="relative bg-background/30 backdrop-blur-sm rounded-lg">
                <div className="p-6">
                  <h2 className="text-2xl font-semibold">Summary & Deployment</h2>
                  <p className="text-muted-foreground mb-6">Review your token configuration and deploy</p>
                  
                  <div className="space-y-6">
                    <FeeBreakdown
                      {...getFeeBreakdownValues()}
                    />

                    {/* Deploy Button */}
                    <div className="flex flex-col space-y-4 w-full">
                      <Button
                        type="submit"
                        disabled={!isConnected || !isCorrectChain || isLoading || isWaitingForReceipt}
                        className="w-full h-12 text-lg font-semibold text-white transition-all duration-300 border-2 hover:opacity-90"
                        style={{
                          background: `linear-gradient(to right, var(--color-header-accent, #0A74FF), var(--color-secondary, #10b981), var(--color-header-accent, #0A74FF))`,
                          borderColor: `var(--color-secondary, #10b981)20`
                        }}
                      >
                        {!isConnected ? "Connect Wallet" : isLoading || isWaitingForReceipt ? (
                          <div className="flex items-center justify-center gap-2">
                            <span>Creating Token</span>
                            <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
                          </div>
                        ) : (
                          "Create Token"
                        )}
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </form>
        </Form>
      </FormProvider>

      {/* Token Creation Success Card - Moved outside the form */}
      {createdTokenDetails && (
        <div className="mt-8">
          <TokenConfirmation
            tokenAddress={createdTokenDetails.address}
            name={createdTokenDetails.name}
            symbol={createdTokenDetails.symbol}
            totalSupply={createdTokenDetails.totalSupply}
            owner={createdTokenDetails.owner}
          />
        </div>
      )}

      {/* Liquidity Addition Success Card */}
      {liquidityDetails && (
        <div className="mt-8">
          <LiquidityConfirmation
            lpAddress={liquidityDetails.lpAddress}
            tokenAddress={liquidityDetails.tokenAddress}
            tokenSymbol={liquidityDetails.tokenSymbol}
            bnbAmount={liquidityDetails.bnbAmount}
            tokenAmount={liquidityDetails.tokenAmount}
          />
        </div>
      )}

      {createdToken && (
        <div className="mt-8">
          <TokenVerificationStatus
            tokenAddress={createdToken.address}
            chainId={chainId}
            tokenSymbol={form.watch("symbol")}
            tokenDecimals={Number(form.watch("decimals"))}
          />
        </div>
      )}
    </div>
  );
}